import React from 'react';
import {Box} from '@mui/material';
import {createHtmlPortalNode, InPortal, OutPortal} from 'react-reverse-portal';
import SourcesSection from './SourcesSection';

const SourcesSectionPortalContext = React.createContext(null);

const useSourcesSectionPortalContext = () => React.useContext(SourcesSectionPortalContext);

export const SourcesSectionPortalProvider = ({children}) => {
    const portalNode = React.useMemo(() => createHtmlPortalNode(), []);
    const [hosts, setHosts] = React.useState({});

    const setHostActive = React.useCallback((hostId, isActive) => {
        setHosts((prev) => {
            if (prev[hostId] === isActive) {
                return prev;
            }

            return {
                ...prev,
                [hostId]: isActive,
            };
        });
    }, []);

    const isActive = React.useMemo(
        () => Object.values(hosts).some(Boolean),
        [hosts]
    );

    const value = React.useMemo(
        () => ({portalNode, setHostActive}),
        [portalNode, setHostActive]
    );

    return (
        <SourcesSectionPortalContext.Provider value={value}>
            <InPortal node={portalNode}>
                <SourcesSection isActive={isActive} mode="files"/>
            </InPortal>
            {children}
        </SourcesSectionPortalContext.Provider>
    );
};

export const SourcesSectionPortalOutlet = ({hostId, isActive}) => {
    const context = useSourcesSectionPortalContext();

    React.useEffect(() => {
        if (!context) {
            return undefined;
        }

        context.setHostActive(hostId, Boolean(isActive));
        return () => {
            context.setHostActive(hostId, false);
        };
    }, [context, hostId, isActive]);

    if (!context) {
        return <SourcesSection isActive={Boolean(isActive)} mode="files"/>;
    }

    if (!isActive) {
        return null;
    }

    return (
        <Box
            sx={{
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                '& > *': {
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <OutPortal node={context.portalNode}/>
        </Box>
    );
};
